/**
 * Headless export runner - mounts hidden grids and builds the workbook.
 *
 * This component renders no visible UI and supports two usage modes:
 *
 * 1. **Pre-fetched data** - pass `coveragesData` to skip the fetch step.
 * 2. **Self-fetch** - omit `coveragesData` and the component loads its own
 *    data via `loadRfp` + `fetchCoveragesData`.
 *
 * The built workbook is passed to `onComplete` - the caller decides
 * whether to download, upload, or both via `useExportActions`.
 *
 * Unmount the component (or set `coveragesData` to `null`) to cancel.
 */

import { logger } from "@platform/browser-logger";
import { toMinutes, useApi } from "@platform/api";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  BTX_CH_BENEFITS_FIELD,
  BTX_CURRENT_CARRIER_POLICY_DROPDOWN,
} from "@platform/feature-flags";
import { GridApi } from "ag-grid-community";
import { FC, Fragment, useCallback, useEffect, useRef, useState } from "react";
import { SummaryGrid } from "@platform/btx-rates";
import { useQueryClient } from "@tanstack/react-query";
import {
  getCandidatePolicies,
  loadRfp,
  queryKeys,
} from "@platform/plan-grid-core";

import { HiddenPlanGrid } from "./HiddenPlanGrid";
import {
  CoverageExportData,
  GridType,
  transformToBtxRatesFormat,
} from "./types";
import { RatesDataFetcher } from "./RatesDataFetcher";
import { fetchCoveragesData } from "./benefits";
import { buildExportWorkbook } from "./orchestrator";

import type ExcelJS from "exceljs";
import type { ExcelRow } from "./rates";

export interface ExportRunnerProps {
  /** Pre-fetched coverage data. When omitted the component fetches its own. */
  coveragesData?: CoverageExportData[];
  /** RFP display name (for filename). Derived from loadRfp when omitted. */
  rfpName?: string;
  ecoRfpId: string;
  brokerPortalRfpId: string;
  /** Optional coverage type filter (only used when self-fetching). */
  coverageTypes?: string[];
  /** Optional per-coverage plan filter (only used when self-fetching). */
  coveragePlanTypes?: Record<string, string[]>;
  onComplete?: (workbook: ExcelJS.Workbook) => void | Promise<void>;
  onError?: (error: Error) => void;
}

const getGridKey = (coverageType: string, gridType: GridType): string =>
  `${coverageType}-${gridType}`;

/**
 * Headless component that executes the full export pipeline.
 *
 * Supports two modes: pass `coveragesData` for pre-fetched data, or omit it
 * to trigger a self-fetch via `loadRfp` + `fetchCoveragesData`. The component
 * renders hidden AG Grid instances, collects their APIs, builds an Excel
 * workbook, downloads it, and uploads to SharePoint.
 */
export const ExportRunner: FC<ExportRunnerProps> = ({
  coveragesData: externalCoveragesData,
  rfpName: externalRfpName,
  ecoRfpId,
  brokerPortalRfpId,
  coverageTypes,
  coveragePlanTypes,
  onComplete,
  onError,
}) => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  const currentCarrierPolicyDropdownEnabled = useFeatureIsOn(
    BTX_CURRENT_CARRIER_POLICY_DROPDOWN,
  );
  // Read only to decide which child benefit-description rows the exported
  // grid renders (passed to HiddenPlanGrid below). Export never saves, so it
  // has no say in which shape is authoritative on write.
  const childAgeBandsFieldEnabled = useFeatureIsOn(BTX_CH_BENEFITS_FIELD);
  const [fetchedCoveragesData, setFetchedCoveragesData] = useState<
    CoverageExportData[] | null
  >(null);
  const [fetchedRfpName, setFetchedRfpName] = useState<string | null>(null);
  const fetchStartedRef = useRef(false);

  // Self-fetch when coveragesData is not provided externally
  useEffect(() => {
    if (externalCoveragesData || fetchStartedRef.current) return;
    fetchStartedRef.current = true;

    const fetchData = async (): Promise<void> => {
      try {
        logger.info("[btx export] ExportRunner self-fetching RFP data");
        const rfp = await loadRfp(ecoRfpId, axiosInstance);

        if (!externalRfpName) {
          setFetchedRfpName(rfp.name);
        }

        const data = await fetchCoveragesData(
          rfp,
          ecoRfpId,
          axiosInstance,
          coverageTypes,
          coveragePlanTypes,
        );
        setFetchedCoveragesData(data);
      } catch (error) {
        logger.error(
          "[btx export] ExportRunner failed to fetch data",
          error as Error,
        );
        onError?.(error as Error);
      }
    };

    fetchData();
  }, [
    externalCoveragesData,
    externalRfpName,
    ecoRfpId,
    axiosInstance,
    coverageTypes,
    coveragePlanTypes,
    onError,
  ]);

  const coveragesData = externalCoveragesData ?? fetchedCoveragesData;
  const rfpName = externalRfpName || fetchedRfpName || "export";

  // Resolve the In-Force Policy Name label (carrier name only) per plan id
  // so we avoid exporting policy numbers when BTX_CURRENT_CARRIER_POLICY_DROPDOWN is on.
  const [resolvedPolicyLabelByPlanId, setResolvedPolicyLabelByPlanId] =
    useState<Record<string, string> | null>(null);
  const policyLabelsStartedRef = useRef(false);

  useEffect(() => {
    if (!currentCarrierPolicyDropdownEnabled || !coveragesData) return;
    if (policyLabelsStartedRef.current) return;
    policyLabelsStartedRef.current = true;

    const resolveLabels = async (): Promise<void> => {
      try {
        const labelByPlanId: Record<string, string> = {};
        await Promise.all(
          coveragesData.map(async ({ coverage, benefitPlanGroups }) => {
            const currentGroups = benefitPlanGroups.filter(
              (group) => group.currentPolicyUuid,
            );
            if (currentGroups.length === 0) return;

            const policiesByPlanType = new Map(
              await Promise.all(
                Array.from(
                  new Set(currentGroups.map((group) => group.productType)),
                ).map(
                  async (planType) =>
                    [
                      planType,
                      await queryClient.ensureQueryData({
                        queryKey: queryKeys.candidatePolicies(
                          coverage.id,
                          planType,
                        ),
                        queryFn: () =>
                          getCandidatePolicies(
                            coverage.id,
                            planType,
                            axiosInstance,
                          ),
                        staleTime: toMinutes(5),
                      }),
                    ] as const,
                ),
              ),
            );

            currentGroups.forEach((group) => {
              const policy = policiesByPlanType
                .get(group.productType)
                ?.find((p) => p.policyUuid === group.currentPolicyUuid);
              if (!policy) return;
              const label = `${policy.carrierName}`;
              group.benefitPlans.forEach((plan) => {
                labelByPlanId[plan.id] = label;
              });
            });
          }),
        );
        setResolvedPolicyLabelByPlanId(labelByPlanId);
      } catch (error) {
        logger.warn(
          "[btx export] Failed to resolve In-Force Policy Name labels",
          {
            error,
          },
        );
        setResolvedPolicyLabelByPlanId({});
      }
    };

    resolveLabels();
  }, [
    currentCarrierPolicyDropdownEnabled,
    coveragesData,
    axiosInstance,
    queryClient,
  ]);

  const [gridApis, setGridApis] = useState<Map<string, GridApi>>(new Map());
  const [ratesSheetData, setRatesSheetData] = useState<
    Map<string, { rows: ExcelRow[] }>
  >(new Map());
  const hasExportedRef = useRef(false);

  const handleGridReady = useCallback(
    (coverageType: string, gridType: GridType, api: GridApi): void => {
      const gridKey = getGridKey(coverageType, gridType);
      logger.info(`[btx export] Grid ready: ${gridKey}`);
      setGridApis((prev) => {
        const next = new Map(prev);
        next.set(gridKey, api);
        return next;
      });
    },
    [],
  );

  const handleRatesDataReady = useCallback(
    (coverageType: string, rows: ExcelRow[]): void => {
      setRatesSheetData((prev) => {
        const next = new Map(prev);
        next.set(coverageType, { rows });
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (!coveragesData) return;

    const expectedBenefitsCount = coveragesData.length;
    const expectedRatesCount = coveragesData.filter(
      (d) => d.benefitPlanGroups && d.benefitPlanGroups.length > 0,
    ).length;
    const expectedSummaryCount = expectedRatesCount;
    const expectedGridCount = expectedBenefitsCount + expectedSummaryCount;

    if (
      gridApis.size !== expectedGridCount ||
      ratesSheetData.size !== expectedRatesCount
    ) {
      return;
    }

    if (
      currentCarrierPolicyDropdownEnabled &&
      resolvedPolicyLabelByPlanId === null
    ) {
      return;
    }

    if (hasExportedRef.current) {
      return;
    }
    hasExportedRef.current = true;

    const performExport = async (): Promise<void> => {
      try {
        logger.info("[btx export] All grids ready, building workbook");

        const workbook = await buildExportWorkbook({
          coveragesData,
          gridApis,
          ratesSheetData,
          rfpName,
          ecoRfpId,
          brokerPortalRfpId,
          axiosInstance,
          queryClient,
        });

        await onComplete?.(workbook);
      } catch (error) {
        logger.error("[btx export] Export error:", error as Error);
        onError?.(error as Error);
      } finally {
        hasExportedRef.current = false;
      }
    };

    performExport();
  }, [
    gridApis,
    ratesSheetData,
    coveragesData,
    rfpName,
    ecoRfpId,
    brokerPortalRfpId,
    axiosInstance,
    queryClient,
    onComplete,
    onError,
    currentCarrierPolicyDropdownEnabled,
    resolvedPolicyLabelByPlanId,
  ]);

  if (!coveragesData) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: "-9999px",
        top: "-9999px",
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
    >
      {coveragesData.map((data) => {
        const hasRatesData =
          data.benefitPlanGroups && data.benefitPlanGroups.length > 0;

        return (
          <Fragment key={data.coverage.type}>
            <HiddenPlanGrid
              coverage={data.coverage}
              benefitPlanGroups={data.benefitPlanGroups}
              onGridReady={(api) =>
                handleGridReady(data.coverage.type, GridType.Benefits, api)
              }
              hideCurrentCarrierRow={currentCarrierPolicyDropdownEnabled}
              resolvedPolicyLabelByPlanId={
                currentCarrierPolicyDropdownEnabled
                  ? (resolvedPolicyLabelByPlanId ?? undefined)
                  : undefined
              }
              childAgeBandsFieldEnabled={childAgeBandsFieldEnabled}
            />
            {hasRatesData && data.benefitPlanGroups && (
              <>
                <RatesDataFetcher
                  benefitPlanGroups={transformToBtxRatesFormat(
                    data.benefitPlanGroups,
                  )}
                  coverageType={data.coverage.type}
                  axiosInstance={axiosInstance}
                  onDataReady={(rows) =>
                    handleRatesDataReady(data.coverage.type, rows)
                  }
                />
                <SummaryGrid
                  benefitPlanGroups={transformToBtxRatesFormat(
                    data.benefitPlanGroups,
                  )}
                  axiosInstance={axiosInstance}
                  onGridReady={(api) =>
                    handleGridReady(data.coverage.type, GridType.Summary, api)
                  }
                />
              </>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};
